// #105 — ONE canonical label for a session type, whatever spelling a row was stored with.
// This is the same matcher as the iPhone app's canonicalSession / sessionLabel / sameSession
// (artifacts/peakrunning-ios/src/theme.ts, !177; the History-filter matcher from !128). Keep the
// two tables in step. Both suites pin the same spellings: src/lib/__tests__/session-types.test.ts
// here and src/theme.test.ts on iPhone.
//
// No data migration: stored rows are read, never rewritten. Canonical labels are Emily's
// (#105 terminology decision): "Intervals" and "Cross-train".

export const CANONICAL_SESSIONS = [
  "Easy Run", "Tempo", "Intervals", "Long Run", "Recovery", "Strength", "Cross-train", "Race", "Other",
] as const;
export type CanonicalSession = (typeof CANONICAL_SESSIONS)[number];

/** Case, spaces, hyphens and underscores are ignored: "Cross-Train" = "cross train" = "Cross-train". */
const norm = (t: string | null | undefined) => (t ?? "").trim().toLowerCase().replace(/[\s_-]+/g, " ");

export const SESSION_ALIASES: Readonly<Record<string, CanonicalSession>> = {
  easy: "Easy Run", long: "Long Run", interval: "Intervals",
  crosstrain: "Cross-train", "cross training": "Cross-train", crosstraining: "Cross-train",
};
const KNOWN = new Map<string, CanonicalSession>([
  ...CANONICAL_SESSIONS.map((k) => [norm(k), k] as const),
  ...Object.entries(SESSION_ALIASES),
]);

/** The known session a stored type means, or null (missing, Rest, or a type we don't know). */
export function canonicalSession(type: string | null | undefined): CanonicalSession | null {
  return KNOWN.get(norm(type)) ?? null;
}

/** The ONE label shown for a stored type: known sessions read canonically; anything else (Rest, Fartlek) as stored. */
export function sessionLabel(type: string): string {
  return canonicalSession(type) ?? type.trim();
}

/** Same session, whatever the spelling; two unknown types match only on the same normalised text. */
export function sameSession(a: string, b: string): boolean {
  return (canonicalSession(a) ?? norm(a)) === (canonicalSession(b) ?? norm(b));
}

/** A picker's starting value: a known spelling selects its canonical option, so a save writes it. */
export function pickerValue(type: string | null | undefined): string | null {
  return type == null ? null : canonicalSession(type) ?? type;
}
